import { useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import { DebouncedFunc } from "lodash";

/**
 * A custom hook that returns a debounced version of a callback function.
 * The debounced function is automatically cleaned up on unmount.
 *
 * @param callback - The function to debounce
 * @param delay - The debounce delay in milliseconds
 * @returns The debounced function
 */
export function useDebounceCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay?: number,
): DebouncedFunc<T> {
  const debounced = useMemo<DebouncedFunc<T>>(() => {
    return debounce(callback, delay || 400);
  }, [callback, delay]);

  useEffect(() => {
    return () => {
      debounced.cancel(); // Cleanup on unmount
    };
  }, [debounced]);

  return debounced;
}
